import { BoxV2 as Box, Flex, useHasPermission } from 'portal-commons';
import { FunctionComponent, useEffect, useState } from 'react';

import BrandAssetRow from './BrandAssetRow';
import { BrandAsset } from '../../types';

const ProgressBar: FunctionComponent = ({ children }) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        width: 0,
        height: 4,
        backgroundColor: 'rgba(0,102,125,0.9)',
        transformOrigin: 'left',
        animation: 'linearProgress 1s linear infinite',
        '@keyframes linearProgress': {
          '0%': { left: '-10%', width: '20%' },
          '20%': { left: '10%', width: '40%' },
          '50%': { left: '40%', width: '50%' },
          '80%': { left: '60%', width: '40%' },
          '100%': { left: '100%', width: '40%' },
        },
      }}
    >
      {children}
    </Box>
  );
};

type BrandAssetListHeader = {
  id: string;
  label: string;
  width?: number;
};

const Headers: BrandAssetListHeader[] = [
  {
    id: 'status',
    label: 'STATUS',
    width: 310,
  },
  {
    id: 'action',
    label: 'ACTION',
    width: 81,
  },
];

const ReadonlyHeaders: BrandAssetListHeader[] = [
  {
    id: 'status',
    label: 'STATUS',
    width: 310,
  },
];

interface BrandAssetListProps {
  title: string;
  assets: BrandAsset[];
  isLoading?: boolean;
  loadingIndex?: number;
  onSelect?: (index: number) => void;
  onVerify?: (index: number) => void;
}

const BrandAssetList: FunctionComponent<BrandAssetListProps> = ({
  title,
  assets,
  isLoading = false,
  loadingIndex,
  onSelect,
  onVerify,
}) => {
  const authorized = useHasPermission('brandDetail.verifyAsset');
  const headers = [
    {
      id: 'name',
      label: title,
    },
    ...(authorized ? Headers : ReadonlyHeaders),
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridAutoRows: '38px',
        gridTemplateColumns: headers
          .map((header) =>
            header.width ? `minmax(${header.width}px, min-content)` : '1fr'
          )
          .join(' '),
        minHeight: '190px',
        maxHeight: '228px',
        borderTopRightRadius: '10px',
        borderTopLeftRadius: '10px',
        fontWeight: 600,
        fontSize: '12px',
        lineHeight: '14px',
        color: '#333E41',
        overflowY: 'auto',
        '@supports (scrollbar-color: auto)': {
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0, 0, 0, 0.25) transparent',
        },
        '@supports selector(::-webkit-scrollbar)': {
          '&::-webkit-scrollbar': {
            width: '8px',
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '4px',
            background: 'rgba(0, 0, 0, 0.25)',
          },
        },
        position: 'relative',
      }}
      data-testid={`${title.toLocaleLowerCase()}AssetList`}
    >
      <Box
        sx={{
          display: 'contents',
          '> *:first-child': {
            borderTopLeftRadius: '10px',
          },
          '> *:last-child': {
            borderTopRightRadius: '10px',
          },
        }}
      >
        {headers.map((header) => (
          <Flex
            key={header.id}
            sx={{
              position: 'sticky',
              top: 0,
              alignItems: 'center',
              paddingLeft: '16px',
              color: '#00667D',
              background: '#E5EFF2',
              fontWeight: 800,
            }}
          >
            {header.label}
          </Flex>
        ))}
      </Box>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 38,
            width: '100%',
            height: 4,
            backgroundColor: 'transparent',
            overflow: 'hidden',
          }}
        >
          <ProgressBar />
        </Box>
      )}
      <Box
        sx={{
          display: 'contents',
          '> *:nth-child(even)': {
            '> *': {
              background: '#F2F3F3',
            },
          },
        }}
      >
        {assets.map((logo, index) => (
          <BrandAssetRow
            key={logo.attachmentUuid}
            asset={logo}
            group={title}
            loading={loadingIndex === index}
            onSelect={() => onSelect?.(index)}
            onVerify={() => onVerify?.(index)}
          />
        ))}
      </Box>
    </Box>
  );
};

export default BrandAssetList;
